import { toDateOnly, toIsoString } from './demoUtils';

export const NOTIFICATION_WINDOWS = [30, 14, 0];

function getPolicyLabel(policy) {
  return (policy.policy_name || '').trim() || policy.insurance_type?.name || 'Policy';
}

export function getNotificationKey(policyId, daysBeforeExpiration) {
  return `policy:${policyId}:${daysBeforeExpiration}`;
}

function getNotificationCreatedAt(policy, daysBeforeExpiration) {
  const expirationDate = toDateOnly(policy.expiration_date);
  if (!expirationDate) {
    return toIsoString(new Date());
  }

  const createdAt = new Date(expirationDate);
  createdAt.setDate(createdAt.getDate() - Math.max(daysBeforeExpiration, 0));
  return createdAt.toISOString();
}

function createNotificationCandidate(policy, daysBeforeExpiration) {
  const policyLabel = getPolicyLabel(policy);
  const isExpiredNotification = daysBeforeExpiration === 0;

  return {
    id: getNotificationKey(policy.id, daysBeforeExpiration),
    policy_id: policy.id,
    user_id: 999,
    notification_type: isExpiredNotification ? 'policy_expired' : 'expiration_warning',
    title: isExpiredNotification
      ? `Policy Expired: ${policy.vendor_name}`
      : `Policy Expiring in ${daysBeforeExpiration} Days: ${policy.vendor_name}`,
    message: isExpiredNotification
      ? `The ${policyLabel} policy for ${policy.vendor_name} has expired.`
      : `The ${policyLabel} policy for ${policy.vendor_name} will expire in ${daysBeforeExpiration} days.`,
    days_before_expiration: daysBeforeExpiration,
    read: false,
    read_at: null,
    link_url: `/demo/policies/${policy.id}`,
    created_at: getNotificationCreatedAt(policy, daysBeforeExpiration),
  };
}

export function buildNotificationCandidates(policies) {
  const candidates = [];

  policies.forEach((policy) => {
    if (policy.status !== 'active') {
      return;
    }

    if (policy.vendor_status !== 'active') {
      return;
    }

    if (NOTIFICATION_WINDOWS.includes(policy.days_until_expiration)) {
      candidates.push(createNotificationCandidate(policy, policy.days_until_expiration));
    }
  });

  return candidates.sort((left, right) => new Date(right.created_at) - new Date(left.created_at));
}

export function mergeNotifications(currentNotifications, candidateNotifications, seedReadKeys = new Set()) {
  const notificationsById = new Map(currentNotifications.map((notification) => [notification.id, notification]));

  return candidateNotifications
    .map((candidate) => {
      const existing = notificationsById.get(candidate.id);
      if (existing) {
        return {
          ...candidate,
          read: existing.read,
          read_at: existing.read_at,
        };
      }

      if (seedReadKeys.has(candidate.id)) {
        return {
          ...candidate,
          read: true,
          read_at: candidate.created_at,
        };
      }

      return candidate;
    })
    .sort((left, right) => new Date(right.created_at) - new Date(left.created_at));
}

export function applyNotificationLedger(candidateNotifications, notificationLedger, seedReadKeys = new Set()) {
  return candidateNotifications.map((candidate) => {
    const ledgerEntry = notificationLedger[candidate.id];

    if (ledgerEntry) {
      return {
        ...candidate,
        read: Boolean(ledgerEntry.read),
        read_at: ledgerEntry.read_at,
      };
    }

    if (seedReadKeys.has(candidate.id)) {
      return {
        ...candidate,
        read: true,
        read_at: candidate.created_at,
      };
    }

    return candidate;
  }).sort((left, right) => new Date(right.created_at) - new Date(left.created_at));
}
